import { useMemo } from "react";

import { useInternalApiUser, useUserDescendants } from "../../../../api/hooks";

import type { UserDescendantsData } from "../../../../api/services";

import { getVisibleGoalAssigneeIds } from "../utils";

type UseMeetingGoalsVisibilityParams = {
	currentUserId: string;
	meetingParticipantIds: string[];
	isAdmin: boolean;
};

type UseMeetingGoalsVisibilityResult = {
	visibleAssigneeIds: string[];
	isLoading: boolean;
};

export const useMeetingGoalsVisibility = ({
	currentUserId,
	meetingParticipantIds,
	isAdmin,
}: UseMeetingGoalsVisibilityParams): UseMeetingGoalsVisibilityResult => {
	const shouldFetchRelationships = Boolean(currentUserId) && !isAdmin;

	const { data: currentUser, isLoading: isUserLoading } = useInternalApiUser(currentUserId, {
		enabled: shouldFetchRelationships,
	});
	const descendantsQuery = useUserDescendants(currentUserId, {
		enabled: shouldFetchRelationships,
	});
	const descendantsData = descendantsQuery.data as UserDescendantsData | undefined;

	const directReportIds = useMemo(() => {
		return (currentUser?.directReports || []).map((report) => report._id);
	}, [currentUser?.directReports]);

	const contributesToIds = useMemo(() => {
		return (currentUser?.contributesTo || []).map((contributor) => contributor._id);
	}, [currentUser?.contributesTo]);

	const descendantIds = descendantsData?.descendants || [];

	const visibleAssigneeIds = useMemo(() => {
		return getVisibleGoalAssigneeIds({
			currentUserId,
			meetingParticipantIds,
			isAdmin,
			directReportIds,
			contributesToIds,
			descendantIds,
		});
	}, [currentUserId, meetingParticipantIds, isAdmin, directReportIds, contributesToIds, descendantIds]);

	return {
		visibleAssigneeIds,
		isLoading: !isAdmin && (isUserLoading || descendantsQuery.isLoading),
	};
};
